// Shared by Cloud Functions and the frontend (imported there as @shared).
// Watching a game: which of a manager's games they haven't seen yet, and
// the timing for replaying a game's play-by-play.
//
// users/{appId}.seenGames: {leagueId: {year, day}}, the latest of the
// manager's own games they've seen (markGamesSeen); users/{appId}
// .askResults: false turns the question off.

// Overtime length in seconds (the engine's TUNING.overtimeSeconds).
const OVERTIME_SECONDS = 300;

// Replay speeds: about how long a whole game takes to watch, in seconds.
// Even Quick leaves time to read every play.
export const REPLAY_SPEEDS = [
  {key: "quick", label: "Quick", seconds: 240},
  {key: "normal", label: "Normal", seconds: 600},
  {key: "slow", label: "Slow", seconds: 1200},
];

// Box score numbers a play can change; each play's `s` holds the ones
// that changed since the play before ({playerId: {stat: n}}), set by the
// engine. Minutes and plus-minus aren't play events.
export const PLAY_STATS = ["pts", "fgm", "fga", "tpm", "tpa", "ftm", "fta",
  "oreb", "dreb", "ast", "stl", "blk", "tov", "pf"];

/**
 * Each player's stats through a play of the replay.
 * @param {object[]} pbp
 * @param {number} upto index of the last play shown
 * @return {object|null} {playerId: {stat: n}}, or null for games played
 *   before plays carried their stats
 */
export function liveBox(pbp, upto) {
  if (!pbp.some((e) => e.s)) return null;
  const box = {};
  for (let i = 0; i <= Math.min(upto, pbp.length - 1); i++) {
    for (const [pid, changes] of Object.entries(pbp[i].s ?? {})) {
      const row = box[pid] ??= Object.fromEntries(PLAY_STATS.map((k) =>
        [k, 0]));
      for (const [k, n] of Object.entries(changes)) row[k] += n;
    }
  }
  return box;
}

/**
 * The play to pause after at halftime: the last play of the first half.
 * @param {object[]} pbp
 * @param {number} minutesPerGame
 * @return {number} its index, or -1
 */
export function halftimeIndex(pbp, minutesPerGame) {
  const half = (minutesPerGame >= 48 ? 4 : 2) / 2;
  return pbp.findIndex((e, i) => e.period === half &&
    pbp[i + 1]?.period === half + 1);
}

/**
 * The team's played games this season that the manager hasn't seen, in
 * order. With nothing remembered for this season, only the latest game
 * day counts as new (so joining a league mid-season doesn't bring up every
 * game played so far).
 * @param {object} args
 * @param {object} args.league
 * @param {object|null} args.schedule summary/schedule
 * @param {string} args.teamId
 * @param {object|null} args.seen seenGames[leagueId] {year, day}
 * @return {object[]} schedule games
 */
export function unseenGames({league, schedule, teamId, seen}) {
  const year = league?.season?.year ?? 1;
  const played = (schedule?.games ?? []).filter((g) => g.result &&
    (g.home === teamId || g.away === teamId))
      .sort((a, b) => a.day - b.day);
  if (!played.length) return [];
  const latest = played.at(-1).day;
  const after = seen?.year === year ? seen.day : latest - 1;
  return played.filter((g) => g.day > after);
}

/**
 * When each play happens, in seconds of game time from the tip.
 * @param {object[]} pbp [{period, clock: "m:ss"}]
 * @param {number} minutesPerGame
 * @return {number[]}
 */
export function gameSeconds(pbp, minutesPerGame) {
  const periods = minutesPerGame >= 48 ? 4 : 2;
  const length = minutesPerGame * 60 / periods;
  const start = (period) => period <= periods ? (period - 1) * length :
    periods * length + (period - periods - 1) * OVERTIME_SECONDS;
  const span = (period) => period <= periods ? length : OVERTIME_SECONDS;
  return pbp.map((e) => {
    const [m, s] = String(e.clock ?? "0:00").split(":").map(Number);
    const left = (m || 0) * 60 + (s || 0);
    return start(e.period) + span(e.period) - left;
  });
}

/**
 * How long to wait before showing each play, in milliseconds, so a whole
 * game takes about `totalSeconds` to watch and plays come at the pace of
 * the game clock. Plays at the same clock time as the one before (a miss
 * and its rebound, free throws) still get a short beat of their own, and a
 * break between periods a longer pause; both come out of the time the
 * clock-paced gaps get, so the total stays close.
 * @param {object[]} pbp
 * @param {number} minutesPerGame
 * @param {number} totalSeconds REPLAY_SPEEDS seconds
 * @param {number} [minMs]
 * @return {number[]} the wait before each play (the first is 0)
 */
export function replayDelays(pbp, minutesPerGame, totalSeconds, minMs = 80) {
  if (!pbp.length) return [];
  const at = gameSeconds(pbp, minutesPerGame);
  const totalMs = totalSeconds * 1000;
  const isBreak = (i) => i > 0 && pbp[i].period !== pbp[i - 1].period;
  const isSame = (i) => i > 0 && !isBreak(i) && at[i] <= at[i - 1];
  let breaks = 0;
  let same = 0;
  for (let i = 1; i < pbp.length; i++) {
    if (isBreak(i)) breaks++;
    else if (isSame(i)) same++;
  }
  const pause = Math.min(2500, totalMs * 0.02);
  // About 60% of an average play's time, at least a quarter second.
  const beat = Math.max(250, totalMs / pbp.length * 0.6);
  const budget = Math.max(totalMs * 0.3,
      totalMs - breaks * pause - same * beat);
  const span = Math.max(1, at.at(-1) - at[0]);
  return pbp.map((e, i) => {
    if (!i) return 0;
    if (isSame(i)) return Math.round(beat);
    const gap = Math.max(0, at[i] - at[i - 1]) / span * budget;
    return Math.max(minMs, Math.round(gap + (isBreak(i) ? pause : 0)));
  });
}
